/**
 * Association Event Component
 *
 * Handles ahoy_association_added and ahoy_association_removed events.
 *
 * Display patterns:
 * - Many cardinality added: "**Actor** added **Acme Corp** to **Companies**"
 * - One cardinality added: "**Actor** set **Company** to **Acme Corp**"
 * - Many cardinality removed: "**Actor** removed **Acme Corp** from **Companies**"
 * - One cardinality removed: "**Actor** cleared **Company**"
 */

import { useCallback, useMemo } from 'react';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';

import {
  ActorName,
  bold,
  InlineText,
  TimelineEvent,
} from '../components/shared';
import { useViewStateLookups } from '../context/TimelineLookupsContext';
import { isAssociationTypeVisible } from '../timelineItemVisibility';
import type { AssociationPayload } from '../types';
import { EVENT_TYPES } from '../types';
import { useEventFormatters } from '../useEventFormatters';
import type { EventComponentProps } from './types';

interface AssociationPayloadInternal extends AssociationPayload {
  internally_added?: {
    target_label?: string;
  };
}

export function AssociationEvent({ event }: EventComponentProps) {
  const { getActorInfo, getObjectLabel } = useEventFormatters();
  const { lookups } = useViewStateLookups();
  const actor = getActorInfo(event);
  const payload = event.payload as AssociationPayloadInternal | undefined;
  const isAdded = event.event_type_name === EVENT_TYPES.ASSOCIATION_ADDED;

  const openSlideout = useOpenSlideoutOptional();

  // Get association type from lookups
  const assocTypeName = payload?.association_type_name ?? '';
  const associationType = lookups.association_types[assocTypeName];

  // Derived before early return so hooks can safely depend on them
  const targetObject = payload?.target_entity_id
    ? lookups.entities[payload.target_entity_id]
    : undefined;
  const isNoteObject = targetObject?.entity_type_name === 'ahoy_note';

  const handleTargetClick = useCallback(() => {
    if (targetObject) {
      openSlideout?.(targetObject.entity_id);
    }
  }, [openSlideout, targetObject]);

  const targetBadgeState = useMemo(
    () =>
      targetObject && !isNoteObject
        ? {
            kind: 'data' as const,
            data: {
              id: targetObject.entity_id,
              entityType: targetObject.entity_type_name,
              displayName: targetObject.ahoy_display_name ?? 'Unknown',
            },
          }
        : null,
    [targetObject, isNoteObject]
  );

  // Hide if association type not in lookups or is internal (fail-closed)
  if (!isAssociationTypeVisible(associationType)) {
    return null;
  }

  // Determine if this is a "many" relationship (one_to_many or many_to_many)
  const isManyCardinality =
    associationType?.cardinality === 'one_to_many' ||
    associationType?.cardinality === 'many_to_many';

  // Get target label from payload or lookup (for fallback and aria-label)
  let targetLabel = 'record';
  if (payload) {
    // First check internally_added for pre-computed label
    if (payload.internally_added?.target_label) {
      targetLabel = payload.internally_added.target_label;
    }
    // Then check payload's target_label
    else if (payload.target_label) {
      targetLabel = payload.target_label;
    }
    // Fall back to lookup
    else if (payload.target_entity_id) {
      const lookedUpLabel = getObjectLabel(payload.target_entity_id);
      if (lookedUpLabel) {
        targetLabel = lookedUpLabel;
      }
    }
  }

  // When the target object is not in lookups (access-controlled) and the
  // association points to a meeting, show "Private meeting" instead of "Unknown"
  if (
    !targetObject &&
    targetLabel === 'Unknown' &&
    associationType?.target_entity_type_name === 'ahoy_meeting'
  ) {
    targetLabel = 'Private meeting';
  }

  // Get the association type label (e.g., "Company", "Deals")
  const relationshipLabel = associationType?.label || 'association';

  const targetDisplay = targetBadgeState ? (
    <EntityBadge
      state={targetBadgeState}
      className='align-middle'
      onClick={openSlideout ? handleTargetClick : undefined}
    />
  ) : (
    bold(targetLabel)
  );

  // Build message based on cardinality
  let messageContent;
  let ariaLabel;

  if (isAdded) {
    if (isManyCardinality) {
      // Many: "Actor added Acme Corp to Companies"
      ariaLabel = `${actor.name} added ${targetLabel} to ${relationshipLabel}`;
      messageContent = (
        <>
          <ActorName actor={actor} /> added {targetDisplay} to{' '}
          {bold(relationshipLabel)}
        </>
      );
    } else {
      // One: "Actor set Company to Acme Corp"
      ariaLabel = `${actor.name} set ${relationshipLabel} to ${targetLabel}`;
      messageContent = (
        <>
          <ActorName actor={actor} /> set {bold(relationshipLabel)} to{' '}
          {targetDisplay}
        </>
      );
    }
  } else {
    if (isManyCardinality) {
      // Many: "Actor removed Acme Corp from Companies"
      ariaLabel = `${actor.name} removed ${targetLabel} from ${relationshipLabel}`;
      messageContent = (
        <>
          <ActorName actor={actor} /> removed {targetDisplay} from{' '}
          {bold(relationshipLabel)}
        </>
      );
    } else {
      // One: "Actor cleared Company"
      ariaLabel = `${actor.name} cleared ${relationshipLabel}`;
      messageContent = (
        <>
          <ActorName actor={actor} /> cleared {bold(relationshipLabel)}
        </>
      );
    }
  }

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Header timestamp={event.occurred_at}>
        <InlineText>{messageContent}</InlineText>
      </TimelineEvent.Header>
    </TimelineEvent>
  );
}
